<template>
  <div v-if="dialogVisible" class="member-center-overlay">
    <div class="member-center-dialog">
      <button class="member-center-close" type="button" @click="closeDialog">
        ✕
      </button>

      <section class="member-center-content">
        <section class="profile-card">
          <div class="profile-card__identity">
            <div class="profile-card__avatar">{{ profileInitial }}</div>
            <div class="profile-card__name-wrap">
              <h1>{{ profileName }}</h1>
              <span v-if="userProfile.roleType != null">{{
                profileAccountLevel
              }}</span>
            </div>
          </div>
          <dl class="profile-card__details">
            <div>
              <dt>{{ $t("header.memberCenter.phone") }}</dt>
              <dd>{{ profilePhone }}</dd>
            </div>
            <div v-if="userProfile.roleType != null">
              <dt>{{ $t("header.memberCenter.accountLevel") }}</dt>
              <dd>{{ profileAccountLevel }}</dd>
            </div>
            <div v-if="isTeamMemberRow(userProfile)">
              <dt>{{ $t("header.memberCenter.role") }}</dt>
              <dd>{{ profileRole }}</dd>
            </div>
            <div v-if="userProfile.parentAdminName">
              <dt>{{ $t("header.memberCenter.parentAdmin") }}</dt>
              <dd>{{ profileParentAdmin }}</dd>
            </div>
          </dl>
          <button
            type="button"
            class="profile-card__edit"
            @click="openProfileEditor"
          >
            {{ $t("header.memberCenter.editProfile") }}
          </button>
        </section>

        <div class="summary-grid">
          <article class="points-card">
            <div class="points-card__total">
              <RollingNumber
                class="points-card__total-value"
                :value="totalPoints"
                :replay-key="dialogVisible"
              />
              <span class="points-card__total-label">总积分</span>
            </div>

            <div class="points-card__divider"></div>

            <div
              class="points-card__breakdown"
              :class="{
                'points-card__breakdown--recharge-only':
                  subscriptionButtonHidden,
              }"
            >
              <div
                v-if="!subscriptionButtonHidden"
                class="points-card__item"
                style="min-width: 15.71429rem;"
              >
                <div class="points-card__item-head">
                  <span class="dot dot--gold"></span>
                  <span>订阅积分</span>
                </div>
                <RollingNumber
                  class="points-card__item-value points-card__item-value--gold"
                  :value="subscriptionPoints"
                  :replay-key="dialogVisible"
                />
                <p class="points-card__item-note">
                  {{ subscriptionPointsNote }}
                </p>
              </div>

              <div class="points-card__item">
                <div class="points-card__item-head">
                  <span class="dot dot--gray"></span>
                  <span>充值积分</span>
                </div>
                <RollingNumber
                  class="points-card__item-value"
                  :value="directPoints"
                  :replay-key="dialogVisible"
                />
                <p class="points-card__item-note">永不清零</p>
              </div>
            </div>
            <div
              v-if="hasCurrentMonthAddSeatsPoints"
              class="points-card__add-seats-notice"
            >
              <span>
                {{
                  $t("header.memberCenter.addSeatsPointsNotice", {
                    date: currentMonthAddSeatsPointsResetDate,
                  })
                }}
              </span>
              <el-tooltip
                :content="$t('header.memberCenter.addSeatsPointsHelp')"
                effect="dark"
                placement="top-start"
                popper-class="add-seats-points-help-tooltip"
              >
                <button
                  type="button"
                  class="points-card__add-seats-help"
                  :aria-label="$t('header.memberCenter.addSeatsPointsHelpAria')"
                  @click.stop
                >
                  <i class="el-icon-question"></i>
                </button>
              </el-tooltip>
            </div>
          </article>

          <article
            class="plan-card"
            :class="{ 'plan-card--active': hasSubscription }"
          >
            <div class="plan-card__header">
              <div>
                <h2>{{ planTitle }}</h2>
                <p class="plan-card__subtitle">{{ planSubtitle }}</p>
              </div>
              <span v-if="hasSubscription" class="plan-card__badge"
                >订阅中</span
              >
              <span v-else class="plan-card__badge plan-card__badge--inactive"
                >未订阅</span
              >
            </div>

            <div class="plan-card__stats">
              <div class="plan-card__stat">
                <span class="plan-card__stat-label"
                  >{{ $t("header.memberCenter.monthlyPoints")
                  }}{{
                    isEnterprise && hasSubscription
                      ? $t("header.memberCenter.monthlyPointsEnterpriseHint")
                      : ""
                  }}</span
                >
                <RollingNumber
                  class="plan-card__stat-value plan-card__stat-value--gold"
                  :value="isEnterprise ? monthlyPoints * seats : monthlyPoints"
                  :replay-key="dialogVisible"
                />
								<div
                v-if="isEnterprise && hasSubscription"
                class="plan-card__stat-label"
              >
                <span>{{ seats }} 席</span>
                <span style="margin: 0 0.14286rem;">×</span>
                <span>{{ monthlyPoints.toLocaleString() }}/月</span>
              </div>
              </div>

              <div class="plan-card__stat">
                <span class="plan-card__stat-label">到期时间</span>
                <span class="plan-card__stat-value">{{
                  expireDateDisplay
                }}</span>
                <span class="plan-card__stat-note">{{
                  remainingDaysText
                }}</span>
              </div>
            </div>
            <button
              type="button"
              class="plan-card__add-action"
              @click="openSubscriptionCenter"
              v-if="!hasSubscription && canShowMembershipPurchase"
            >
              开通套餐
            </button>
            <button
              type="button"
              class="plan-card__action"
              @click="openSubscriptionCenter"
              v-if="hasSubscription && canShowMembershipPurchase"
            >
              升级套餐
            </button>
            <button
              type="button"
              class="plan-card__seat-action"
              @click="openAddSeatsDialog"
              v-if="isEnterprise && hasSubscription && isAdmin"
            >
              {{ $t("header.memberCenter.addSeats") }}
            </button>
          </article>
        </div>

        <nav class="record-tabs">
          <button
            v-for="tab in recordTabs"
            :key="tab.key"
            type="button"
            class="record-tabs__item"
            :class="{ active: activeRecordTab === tab.key }"
            @click="switchRecordTab(tab.key)"
          >
            <span class="record-tabs__icon" v-html="tab.icon"></span>
            <span>{{ tab.label }}</span>
            <span
              v-if="tab.key === 'distribution' && distributionTag"
              class="record-tabs__tag"
            >
              {{ distributionTag }}
            </span>
          </button>
        </nav>

        <div class="records-panel">
          <div class="records-toolbar">
            <span class="records-toolbar__count"
              >共 {{ displayTotal }} 条记录</span
            >
            <label class="records-search">
              <i class="el-icon-search records-search__icon"></i>
              <input
                v-model="searchKeyword"
                type="search"
                :placeholder="searchPlaceholder"
                @input="handleSearchInput"
              />
            </label>
          </div>

          <div
            class="records-table-wrap"
            v-loading="recordsLoading"
            element-loading-background="rgba(0, 0, 0, 0.8)"
          >
            <table class="records-table">
              <thead>
                <tr>
                  <th v-for="column in activeColumns" :key="column.key">
                    {{ column.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!recordsLoading && displayRows.length === 0">
                  <td
                    :colspan="activeColumns.length"
                    class="records-table__empty"
                  >
                    {{ emptyText }}
                  </td>
                </tr>
                <tr
                  v-for="(row, index) in displayRows"
                  :key="getRowKey(row, index)"
                >
                  <td v-for="column in activeColumns" :key="column.key">
                    <span
                      v-if="column.key === 'status'"
                      :class="getStatusClass(row.status, activeRecordTab)"
                    >
                      {{ formatStatus(row.status, activeRecordTab) }}
                    </span>
                    <span
                      v-else-if="
                        column.key === 'points' ||
                        column.key === 'pointsObtained'
                      "
                      :class="getRecordPointsClass(column.key)"
                    >
                      {{ formatTableCell(row, column.key) }}
                    </span>
                    <span
                      v-else-if="column.key === 'integralChange'"
                      :class="getPointsClass(row.integralChange)"
                    >
                      {{ formatTableCell(row, column.key) }}
                    </span>
                    <span
                      v-else-if="column.key === 'pointsType'"
                      :class="getIntegralSortClass(row)"
                    >
                      {{ formatTableCell(row, column.key) }}
                    </span>
                    <span v-else>{{ formatTableCell(row, column.key) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="displayTotal > 0" class="records-pagination">
            <span class="records-pagination__total"
              >共 {{ displayTotal }} 条</span
            >
            <button
              type="button"
              class="records-pagination__nav"
              :disabled="recordsPageNum <= 1"
              @click="goRecordsPage(recordsPageNum - 1)"
            >
              ‹
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              type="button"
              class="records-pagination__page"
              :class="{ active: page === recordsPageNum }"
              @click="goRecordsPage(page)"
            >
              {{ page }}
            </button>
            <button
              type="button"
              class="records-pagination__nav"
              :disabled="recordsPageNum >= totalPages"
              @click="goRecordsPage(recordsPageNum + 1)"
            >
              ›
            </button>
          </div>
        </div>
      </section>
    </div>

    <div
      v-if="profileEditVisible"
      class="profile-editor-backdrop"
      @click.self="closeProfileEditor"
      @keydown.esc="closeProfileEditor"
    >
      <aside
        class="profile-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-editor-title"
      >
        <header class="profile-editor__header">
          <div>
            <h2 id="profile-editor-title">
              {{ $t("header.memberCenter.editProfile") }}
            </h2>
            <p>{{ $t("header.memberCenter.editProfileHint") }}</p>
          </div>
          <button
            type="button"
            class="profile-editor__close"
            :aria-label="$t('header.memberCenter.closeEditor')"
            @click="closeProfileEditor"
          >
            &times;
          </button>
        </header>

        <form class="profile-editor__form" @submit.prevent="saveProfile">
          <label class="profile-editor__field">
            <span>
              {{ $t("header.memberCenter.name") }}
              <em class="profile-editor__required" aria-hidden="true">*</em>
            </span>
            <input
              v-model.trim="editName"
              type="text"
              required
              maxlength="20"
              :placeholder="$t('header.memberCenter.namePlaceholder')"
              autofocus
            />
            <small>{{ editName.length }} / 20</small>
          </label>

          <div class="profile-editor__field">
            <span>{{ $t("header.memberCenter.phone") }}</span>
            <div class="profile-editor__readonly">
              <span>{{ profileEditorPhone }}</span>
              <button
                type="button"
                class="profile-editor__phone-toggle"
                @click="phoneVisible = !phoneVisible"
              >
                {{
                  $t(
                    phoneVisible
                      ? "header.memberCenter.hidePhone"
                      : "header.memberCenter.showPhone",
                  )
                }}
              </button>
            </div>
          </div>

          <div
            v-for="item in readonlyProfileFields"
            :key="item.key"
            class="profile-editor__field"
          >
            <span>{{ item.label }}</span>
            <div class="profile-editor__readonly">
              <span>{{ item.value }}</span>
            </div>
          </div>

          <p class="profile-editor__readonly-hint">
            {{ $t("header.memberCenter.readonlyHint") }}
          </p>

          <footer class="profile-editor__footer">
            <button type="button" @click="closeProfileEditor">
              {{ $t("common.cancel") }}
            </button>
            <button
              type="submit"
              class="profile-editor__save"
              :disabled="!canSaveProfile"
            >
              {{ $t("common.saveChanges") }}
            </button>
          </footer>
        </form>
      </aside>
    </div>

    <add-seats-pay-dialog
      :visible.sync="addSeatsDialogVisible"
      :period-type="periodType"
      @success="handleAddSeatsSuccess"
    />
  </div>
</template>

<script>
import AddSeatsPayDialog from "@/views/memberCenter/AddSeatsPayDialog.vue";
import RollingNumber from "@/components/RollingNumber.vue";
import {
  VIP_TYPE,
  accountTypeToClientType,
  formatIntegralUnitPriceYuan,
  formatPrice,
  formatPriceFromFen,
  formatPointPrice,
  getCycleLabel,
  isApiSuccess,
  isSubscriptionButtonHidden,
  periodTypeToCycle,
  syncSubscriptionAccountTypeFromUser,
  persistUserFields,
  getPeriodUnit,
  mapUserSubscriptionInfo,
  matchPlanByNameAndPeriodType,
} from "@/utils/subscription";
import { canPurchaseMembership, isSuperAdmin, isTeamMemberRow } from "@/utils/accountRole";

const RECORD_TABS = [
  {
    key: "recharge",
    label: "积分充值记录",
    icon: "&#128179;",
    logType: 2,
    api: true,
    searchPlaceholder: "搜索...",
    emptyText: "暂无积分充值记录",
    columns: [
      { key: "time", label: "时间" },
      { key: "orderNo", label: "订单号" },
      { key: "amount", label: "充值金额" },
      { key: "pointsObtained", label: "获得积分" },
      { key: "paymentMethod", label: "支付方式" },
    ],
  },
  {
    key: "subscription",
    label: "会员订阅记录",
    icon: "&#9733;",
    api: false,
    searchPlaceholder: "搜索...",
    emptyText: "暂无会员订阅记录",
    columns: [
      { key: "purchaseTime", label: "购买时间" },
      { key: "planName", label: "套餐" },
      { key: "cycle", label: "周期" },
      { key: "points", label: "获得积分" },
      { key: "amount", label: "支付金额" },
      { key: "expireTime", label: "到期时间" },
      { key: "status", label: "状态" },
    ],
  },
  {
    key: "distribution",
    label: "积分发放记录",
    icon: "&#128230;",
    api: false,
    searchPlaceholder: "搜索...",
    emptyText: "暂无积分发放记录",
    columns: [
      { key: "periodLabel", label: "期数" },
      { key: "distributeDate", label: "发放日期" },
      { key: "points", label: "积分" },
      { key: "status", label: "状态" },
    ],
  },
  {
    key: "consumption",
    label: "积分消耗记录",
    icon: "&#128200;",
    logType: 1,
    api: true,
    searchPlaceholder: "搜索操作或模型...",
    emptyText: "暂无积分消耗记录",
    columns: [
      { key: "serial", label: "序号" },
      { key: "time", label: "时间" },
      { key: "operation", label: "操作" },
      { key: "integralChange", label: "积分变更" },
      { key: "pointsType", label: "积分类型" },
      { key: "payTypeLabel", label: "操作类型" },
      { key: "balance", label: "余额" },
      { key: "model", label: "模型" },
      { key: "resolution", label: "分辨率/清晰度" },
      { key: "durationQuantity", label: "时长/数量" },
    ],
  },
];

const LEGACY_TAB_MAP = {
  subscription: "recharge",
  usage: "consumption",
};

export default {
  name: "MemberCenterPanel",
  components: {
    AddSeatsPayDialog,
    RollingNumber,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initialTab: {
      type: String,
      default: "recharge",
    },
    // 从套餐弹窗跳转时，打开个人中心后自动拉起新增席位
    autoOpenAddSeats: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeRecordTab: "recharge",
      totalPoints: 0,
      subscriptionPoints: 0,
      hasCurrentMonthAddSeatsPoints: false,
      currentMonthAddSeatsPointsResetDate: "",
      originalPrice: 0,
      directPoints: 0,
      subscribed: false,
      planName: "",
      periodType: null,
      monthlyPoints: 0,
      seats: 1,
      addSeatsDialogVisible: false,
      nextCreditDate: "",
      endDate: "",
      remainingDays: null,
      paidAmount: 0,
      pointsGivenMonths: 0,
      totalMonths: 0,
      recordTabs: RECORD_TABS,
      searchKeyword: "",
      recordsPageNum: 1,
      recordsPageSize: 10,
      recordsLoading: false,
      apiList: [],
      apiTotal: 0,
      subscriptionRecords: [],
      distributionRecords: [],
      userProfile: {},
      profileEditVisible: false,
      editName: "",
      phoneVisible: false,
    };
  },
  computed: {
    profileName() {
      return (
        this.userProfile.realName ||
        this.userProfile.nickname ||
        this.userProfile.username ||
        "--"
      );
    },
    profileInitial() {
      return String(this.profileName).trim().charAt(0).toUpperCase() || "U";
    },
    profilePhone() {
      const value = String(
        this.userProfile.phone || this.userProfile.username || "",
      );
      if (/^\d{11}$/.test(value)) {
        return `${value.slice(0, 3)} **** ${value.slice(-4)}`;
      }
      return value || "--";
    },
    profileEditorPhone() {
      if (!this.phoneVisible) return this.profilePhone;
      return String(
        this.userProfile.phone || this.userProfile.username || "--",
      );
    },
    profileAccountLevel() {
      const accountTypeKey = {
        0: "member",
        1: "superAdmin",
        2: "levelOneAdmin",
        3: "levelTwoAdmin",
      }[Number(this.userProfile.roleType)];
      return accountTypeKey
        ? this.$t(`teamManagementPage.accountTypes.${accountTypeKey}`)
        : "--";
    },
    profileRole() {
      const roleKey = { 1: "producer", 2: "director", 3: "operator" }[
        Number(this.userProfile.roleId)
      ];
      return roleKey ? this.$t(`teamManagementPage.roles.${roleKey}`) : "--";
    },
    profileParentAdmin() {
      return this.userProfile.parentAdminName || "--";
    },
    readonlyProfileFields() {
      const fields = [
        {
          key: "accountLevel",
          label: this.$t("header.memberCenter.accountLevel"),
          value: this.profileAccountLevel,
        },
      ];
      // 管理账号不展示业务角色
      if (isTeamMemberRow(this.userProfile)) {
        fields.push({
          key: "role",
          label: this.$t("header.memberCenter.role"),
          value: this.profileRole,
        });
      }
      fields.push({
        key: "parentAdmin",
        label: this.$t("header.memberCenter.parentAdmin"),
        value: this.profileParentAdmin,
      });
      return fields;
    },
    canSaveProfile() {
      const name = this.editName.trim();
      return Boolean(
        name && name !== String(this.userProfile.realName || "").trim(),
      );
    },
    subscriptionButtonHidden() {
      return isSubscriptionButtonHidden();
    },
    // 开通/升级套餐入口：个人客户或超级管理员
    canShowMembershipPurchase() {
      try {
        const user = JSON.parse(window.localStorage.getItem("user") || "{}");
        return !this.subscriptionButtonHidden && canPurchaseMembership(user);
      } catch (error) {
        return !this.subscriptionButtonHidden && canPurchaseMembership();
      }
    },
    isEnterprise() {
      const accountType = this.$store.state.subscriptionAccountType;
      return accountType === "enterprise" || accountType === "team";
    },
    // 新增席位：企业仅超级管理员可操作
    isAdmin() {
      try {
        const user = JSON.parse(window.localStorage.getItem("user") || "{}");
        return isSuperAdmin(user);
      } catch (error) {
        return isSuperAdmin();
      }
    },
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    planList() {
      return this.$store.state.subscriptionPlans || [];
    },
    currentPlan() {
      return matchPlanByNameAndPeriodType(
        this.planList,
        this.planName,
        this.periodType,
      );
    },
    subscriptionCycle() {
      return periodTypeToCycle(this.periodType);
    },
    hasSubscription() {
      return !this.subscriptionButtonHidden && Boolean(this.subscribed);
    },
    planTitle() {
      if (this.hasSubscription && this.planName) {
        return `${this.planName} ${
          this.isEnterprise ? `·${this.seats}席位` : ""
        }`;
      }
      return "暂无订阅";
    },
    planSubtitle() {
      if (!this.hasSubscription) {
        return "前往订阅中心选择适合你的方案";
      }
      const cycle = this.subscriptionCycle;
      const unit = getPeriodUnit(cycle);
      if (this.originalPrice) {
        return `¥${formatPriceFromFen(this.originalPrice)}/${unit}${
          this.isEnterprise ? `/席` : ""
        } · 1积分=${this.formatPlanIntegralUnitPrice()}元`;
      }
      const paid = Number(this.paidAmount || 0) / 100;
      console.log(paid, "paid");
      if (paid > 0) {
        return `实付 ¥${paid}/${unit}`;
      }
      return "会员权益生效中";
    },
    subscriptionPointsNote() {
      const resetDate = this.nextCreditDate || this.endDate;
      if (!this.hasSubscription || !resetDate) {
        return "月底清零 · 优先消耗";
      }
      return this.$t("header.memberCenter.subscriptionPointsResetNote", {
        date: String(resetDate).slice(0, 10),
      });
    },
    expireDateDisplay() {
      if (!this.hasSubscription || !this.endDate) {
        return "—";
      }
      return String(this.endDate).slice(0, 10);
    },
    remainingDaysText() {
      if (!this.hasSubscription) {
        return "开通订阅后可查看";
      }
      const days = Number(this.remainingDays);
      if (Number.isNaN(days)) {
        return "—";
      }
      if (days < 0) {
        return "已到期";
      }
      return `剩余 ${days} 天`;
    },
    distributionTag() {
      if (!this.hasSubscription || !this.totalMonths) {
        return "";
      }
      return `${getCycleLabel(this.subscriptionCycle)}·${this.totalMonths}期`;
    },
    activeTabConfig() {
      return (
        this.recordTabs.find((tab) => tab.key === this.activeRecordTab) ||
        this.recordTabs[0]
      );
    },
    activeColumns() {
      return this.activeTabConfig.columns || [];
    },
    searchPlaceholder() {
      return this.activeTabConfig.searchPlaceholder || "搜索...";
    },
    emptyText() {
      return this.activeTabConfig.emptyText || "暂无记录";
    },
    isApiTab() {
      return Boolean(this.activeTabConfig.api);
    },
    filteredMockRows() {
      const keyword = this.searchKeyword.trim().toLowerCase();
      let rows = [];

      if (this.activeRecordTab === "subscription") {
        rows = this.subscriptionRecords;
      } else if (this.activeRecordTab === "distribution") {
        rows = this.distributionRecords;
      }

      if (!keyword) {
        return rows;
      }

      return rows.filter((row) =>
        Object.values(row).some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(keyword),
        ),
      );
    },
    filteredApiRows() {
      const keyword = this.searchKeyword.trim().toLowerCase();
      if (!keyword) {
        return this.apiList;
      }

      return this.apiList.filter((row) => {
        const haystack = [
          row.payDescription,
          row.model,
          row.resolution,
          row.duration,
          row.paymentType,
          row.pkId,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(keyword);
      });
    },
    displayRows() {
      if (this.isApiTab) {
        return this.filteredApiRows.map((row, index) => ({
          ...row,
          __serial:
            (this.recordsPageNum - 1) * this.recordsPageSize + index + 1,
        }));
      }

      const start = (this.recordsPageNum - 1) * this.recordsPageSize;
      return this.filteredMockRows.slice(start, start + this.recordsPageSize);
    },
    displayTotal() {
      if (this.isApiTab) {
        return this.searchKeyword.trim()
          ? this.filteredApiRows.length
          : this.apiTotal;
      }
      return this.filteredMockRows.length;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.displayTotal / this.recordsPageSize));
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.recordsPageNum;
      const pages = [];
      const start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      for (let page = start; page <= end; page += 1) {
        pages.push(page);
      }
      return pages;
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.activeRecordTab = this.normalizeInitialTab(this.initialTab);
        this.searchKeyword = "";
        this.recordsPageNum = 1;
        this.refreshData();
        // 套餐页「新增席位」跳转：个人中心打开后立刻拉起增席弹窗
        this.tryOpenAddSeatsFromSubscription();
      } else {
        // 关闭个人中心时同步关掉增席弹窗，避免残留遮罩
        this.addSeatsDialogVisible = false;
      }
    },
    initialTab(value) {
      if (this.visible) {
        this.activeRecordTab = this.normalizeInitialTab(value);
        this.searchKeyword = "";
        this.recordsPageNum = 1;
        this.fetchActiveRecords();
      }
    },
    autoOpenAddSeats(value) {
      // 个人中心已打开时再收到增席指令，补一次自动打开
      if (value && this.visible) {
        this.tryOpenAddSeatsFromSubscription();
      }
    },
  },
  methods: {
    // 模板判断普通用户才展示业务角色
    isTeamMemberRow,
    // 1积分单价取当前套餐接口字段 integralUnitPrice（分/积分），转元展示
    formatPlanIntegralUnitPrice() {
      const plan = this.currentPlan || {};
      return formatIntegralUnitPriceYuan(plan.integralUnitPrice);
    },
    normalizeInitialTab(tab) {
      return LEGACY_TAB_MAP[tab] || tab || "recharge";
    },
    closeDialog() {
      this.profileEditVisible = false;
      this.dialogVisible = false;
      this.$emit("close");
    },
    loadUserProfile() {
      try {
        this.userProfile = JSON.parse(
          window.localStorage.getItem("user") || "{}",
        );
      } catch (error) {
        this.userProfile = {};
      }
    },
    openProfileEditor() {
      this.editName = String(this.userProfile.realName || "");
      this.phoneVisible = false;
      this.profileEditVisible = true;
    },
    closeProfileEditor() {
      this.profileEditVisible = false;
    },
    saveProfile() {
      if (!this.canSaveProfile) return;
      const realName = this.editName.trim();
      this.$axios
        .post(this.$store.state.serverUrl + "ytb/user/real-name/update", {
          realName,
        })
        .then((res) => {
          if (!isApiSuccess(res.data.code)) {
            this.$message.error(
              res.data.message || this.$t("teamManagementPage.messages.failed"),
            );
            return;
          }
          persistUserFields({ realName });
          this.userProfile = { ...this.userProfile, realName };
          this.profileEditVisible = false;
          this.$message.success(this.$t("header.memberCenter.saveSuccess"));
        })
        .catch((error) => {
          this.$message.error(
            error.message || this.$t("teamManagementPage.messages.failed"),
          );
        });
    },
    openSubscriptionCenter() {
      this.$emit("open-subscription");
    },
    openAddSeatsDialog() {
      this.addSeatsDialogVisible = true;
    },
    // 套餐页已按同一套条件展示入口，此处不再二次拦截，避免个人中心资料尚未返回时漏开弹窗
    tryOpenAddSeatsFromSubscription() {
      if (!this.autoOpenAddSeats) {
        return;
      }
      this.$nextTick(() => {
        this.addSeatsDialogVisible = true;
        this.$emit("add-seats-opened");
      });
    },
    handleAddSeatsSuccess() {
      this.addSeatsDialogVisible = false;
      this.refreshData();
      if (this.$message) {
        this.$message.success(`支付成功，已新增席位`);
      }
    },
    switchRecordTab(tabKey) {
      if (this.activeRecordTab === tabKey) {
        return;
      }
      this.activeRecordTab = tabKey;
      this.searchKeyword = "";
      this.recordsPageNum = 1;
      this.fetchActiveRecords();
    },
    handleSearchInput() {
      this.recordsPageNum = 1;
      if (this.isApiTab) {
        return;
      }
    },
    refreshData() {
      this.loadUserProfile();
      syncSubscriptionAccountTypeFromUser(this.$store);
      const clientType = accountTypeToClientType(
        this.$store.state.subscriptionAccountType,
      );
      this.$store.dispatch("fetchSubscriptionPlans", clientType);
      this.fetchPersonalCenterInfo();
      this.fetchActiveRecords();
    },
    fetchPersonalCenterInfo() {
      this.$axios
        .get(this.$store.state.serverUrl + "ytb/subscription/my/info")
        .then((res) => {
          if (!isApiSuccess(res.data.code)) {
            return;
          }
          const data = res.data.data || {};
          this.userProfile = data;
          this.totalPoints = Number(data.totalPoints || 0);
          this.subscriptionPoints = Number(data.vipIntegral || 0);
          this.directPoints = Number(data.topUpIntegral || 0);
          this.hasCurrentMonthAddSeatsPoints = Boolean(
            data.hasCurrentMonthAddSeatsPoints,
          );
          this.currentMonthAddSeatsPointsResetDate =
            data.currentMonthAddSeatsPointsResetDate || "";
          this.subscribed = Boolean(data.subscribed);
          this.planName = data.planName || "";
          this.periodType = data.periodType;
          this.monthlyPoints = Number(data.monthlyPoints || 0);
          this.seats = Number(data.seats || 1);
          this.originalPrice = Number(data.originalPrice || 0);
          this.nextCreditDate = data.nextCreditDate || "";
          this.endDate = data.endDate || "";
          this.remainingDays = data.remainingDays;
          this.paidAmount = Number(data.paidAmount || 0);
          this.pointsGivenMonths = Number(data.pointsGivenMonths || 0);
          this.totalMonths = Number(data.totalMonths || 0);
          const subscriptionInfo = mapUserSubscriptionInfo(data);
          this.$store.commit("setUserSubscriptionInfo", {
            ...subscriptionInfo,
            vipType: subscriptionInfo.subscribed
              ? VIP_TYPE.ACTIVE
              : VIP_TYPE.NONE,
          });
        });
    },
    fetchActiveRecords() {
      if (this.isApiTab) {
        this.fetchApiRecords();
        return;
      }
      if (this.activeRecordTab === "subscription") {
        this.fetchSubscriptionOrders();
        return;
      }
      if (this.activeRecordTab === "distribution") {
        this.fetchDistributionRecords();
        return;
      }
      this.recordsLoading = false;
      this.apiList = [];
      this.apiTotal = 0;
    },
    fetchSubscriptionOrders() {
      this.recordsLoading = true;
      this.apiList = [];
      this.apiTotal = 0;
      this.$axios
        .get(this.$store.state.serverUrl + "ytb/subscription/my/orders")
        .then((res) => {
          if (!isApiSuccess(res.data.code)) {
            this.subscriptionRecords = [];
            return;
          }
          const list = res.data.data || [];
          this.subscriptionRecords = list.map((item) => ({
            id: item.id,
            purchaseTime: item.createTime,
            planName: item.planName,
            cycle: getCycleLabel(periodTypeToCycle(item.periodType)),
            points: item.totalPoints,
            amount: Number(item.paidAmount || 0) / 100,
            expireTime: item.endDate ? String(item.endDate).slice(0, 10) : "",
            status: item.status,
          }));
        })
        .finally(() => {
          this.recordsLoading = false;
        });
    },
    fetchDistributionRecords() {
      this.recordsLoading = true;
      this.apiList = [];
      this.apiTotal = 0;
      this.$axios
        .get(this.$store.state.serverUrl + "ytb/subscription/my/issuance")
        .then((res) => {
          if (!isApiSuccess(res.data.code)) {
            this.distributionRecords = [];
            return;
          }
          const list = res.data.data || [];
          this.distributionRecords = list.map((item) => ({
            periodLabel: item.period,
            distributeDate: item.issueDate,
            points: item.points,
            status: item.status,
          }));
        })
        .finally(() => {
          this.recordsLoading = false;
        });
    },
    fetchApiRecords() {
      this.recordsLoading = true;
      this.$axios({
        method: "post",
        url: this.$store.state.serverUrl + "ytb/order/select/order/log",
        data: {
          logType: this.activeTabConfig.logType,
          pageNum: this.recordsPageNum,
          pageSize: this.recordsPageSize,
        },
      })
        .then((res) => {
          if (res.data.code === 200) {
            const data = res.data.data || {};
            this.apiList = data.list || [];
            this.apiTotal = Number(data.total || 0);
          }
        })
        .finally(() => {
          this.recordsLoading = false;
        });
    },
    goRecordsPage(page) {
      const nextPage = Math.min(Math.max(page, 1), this.totalPages);
      if (nextPage === this.recordsPageNum) {
        return;
      }
      this.recordsPageNum = nextPage;
      if (this.isApiTab) {
        this.fetchApiRecords();
      }
    },
    getRowKey(row, index) {
      return row.pkId || row.id || row.periodLabel || row.purchaseTime || index;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("zh-CN");
    },
    formatTableCell(row, key) {
      if (key === "time") {
        return this.formatUsageTime(row.gmtCreate);
      }
      if (key === "purchaseTime") {
        return this.formatUsageTime(row.purchaseTime);
      }
      if (key === "distributeDate") {
        return this.formatUsageDate(row.distributeDate);
      }
      if (key === "amount") {
        if (this.activeRecordTab === "subscription") {
          return `¥${this.formatNumber(row.amount)}`;
        }
        if (row.totalMoney != null && row.totalMoney !== "") {
          return row.totalMoney;
        }
        return `¥${this.formatNumber(Number(row.amount || 0) / 100)}`;
      }
      if (key === "pointsObtained") {
        const integral = Number(row.integralNum || 0);
        const give = Number(row.giveNum || 0);
        return `+${this.formatNumber(integral + give)}`;
      }
      if (key === "points") {
        return this.formatNumber(row.points);
      }
      if (key === "paymentMethod") {
        return this.formatPaymentType(row.paymentType);
      }
      if (key === "periodLabel") {
        return row.periodLabel || "—";
      }
      if (key === "expireTime") {
        return row.expireTime ? this.formatUsageDate(row.expireTime) : "—";
      }
      if (key === "cycle") {
        return row.cycle || "—";
      }
      if (key === "planName") {
        return row.planName || "—";
      }
      if (key === "serial") {
        return row.__serial ?? "—";
      }
      if (key === "operation") {
        return this.formatOperation(row);
      }
      if (key === "integralChange") {
        return this.formatPointsChange(row.integralChange);
      }
      if (key === "pointsType") {
        return this.getUsageType(row);
      }
      if (key === "balance") {
        const before = row.beforeIntegralNum;
        const change = row.integralChange;
        if (
          before !== null &&
          before !== undefined &&
          before !== "" &&
          change !== null &&
          change !== undefined &&
          change !== ""
        ) {
          const beforeNum = Number(before);
          const changeNum = Number(change);
          if (!Number.isNaN(beforeNum) && !Number.isNaN(changeNum)) {
            return this.formatNumber(beforeNum + changeNum);
          }
        }
        const balance =
          row.integralBalance ?? row.balance ?? row.surplusIntegral ?? null;
        return balance === null || balance === undefined || balance === ""
          ? "—"
          : this.formatNumber(balance);
      }
      if (key === "model") {
        return this.formatModel(row.model);
      }
      if (key === "resolution") {
        return row.resolution || "—";
      }
      if (key === "durationQuantity") {
        return this.formatDurationQuantity(row);
      }
      return row[key] ?? "—";
    },
    formatPaymentType(value) {
      const type = Number(value);
      if (type === 1) {
        return "微信支付";
      }
      if (type === 2) {
        return "支付宝";
      }
      if (value === "微信") {
        return "微信支付";
      }
      if (value === "对公转账" || value === "对公支付" || value === 3) {
        return "对公转账";
      }
      return value || "—";
    },
    formatUsageTime(value) {
      if (!value) {
        return "—";
      }
      const date = new Date(String(value).replace(/-/g, "/"));
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      return `${month}-${day} ${hour}:${minute}`;
    },
    formatUsageDate(value) {
      if (!value) {
        return "—";
      }
      const date = new Date(String(value).replace(/-/g, "/"));
      if (Number.isNaN(date.getTime())) {
        return String(value).slice(0, 10);
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    formatOperation(row) {
      const description = row.payDescription || "—";
      const extras = [row.resolution, row.duration]
        .filter((item) => item !== null && item !== undefined && item !== "")
        .join(" ");
      if (!extras || description.includes(extras)) {
        return description;
      }
      return `${description} ${extras}`.trim();
    },
    formatModel(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      return value;
    },
    formatDurationQuantity(row) {
      if (row.duration) {
        return row.duration + "秒";
      }
      if (row.quantity || row.num || row.count) {
        const count = Number(row.quantity || row.num || row.count);
        return `${count}张`;
      }
      if (row.payDescription && row.payDescription.includes("图")) {
        return "1张";
      }
      return "—";
    },
    formatPointsChange(value) {
      const number = Number(value || 0);
      if (number > 0) {
        return `+${this.formatNumber(number)}`;
      }
      return this.formatNumber(number);
    },
    getPointsClass(value) {
      const number = Number(value || 0);
      if (number > 0) {
        return "records-table__points records-table__points--plus";
      }
      if (number < 0) {
        return "records-table__points records-table__points--minus";
      }
      return "records-table__points";
    },
    getUsageType(row) {
      const sort = Number(row.integralSort);
      if (sort === 0) {
        return "充值";
      }
      if (sort === 1) {
        return "订阅";
      }
      return "—";
    },
    getIntegralSortClass(row) {
      const sort = Number(row.integralSort);
      if (sort === 0) {
        return "records-table__integral-type records-table__integral-type--recharge";
      }
      if (sort === 1) {
        return "records-table__integral-type records-table__integral-type--subscription";
      }
      return "records-table__integral-type";
    },
    formatStatus(status, tabKey) {
      const value = Number(status);
      if (tabKey === "subscription") {
        if (value === 1) {
          return "订阅中";
        }
        if (value === 2) {
          return "已升档";
        }
        if (value === 0) {
          return "已过期";
        }
      }
      if (tabKey === "distribution") {
        if (value === 1) {
          return "已到账";
        }
        if (value === 0) {
          return "待发放";
        }
        if (value === 2) {
          return "订阅已失效";
        }
      }
      return status ?? "—";
    },
    getStatusClass(status, tabKey) {
      const value = Number(status);
      if (tabKey === "subscription" && value === 1) {
        return "records-status records-status--active";
      }
      if (tabKey === "distribution" && value === 1) {
        return "records-status records-status--received";
      }
      return "records-status records-status--muted";
    },
    getRecordPointsClass(key) {
      return [
        "records-table__points",
        key === "pointsObtained"
          ? "records-table__points--plus"
          : "records-table__points--gold",
      ];
    },
  },
};
</script>

<style lang="less" scoped>
/* 尺寸按设计稿 px/14 转 rem；字号用 max(12PX, Nrem) 保证小屏不低于 12px */

.member-center-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  overflow: auto;
  background: rgba(0, 0, 0, 0.96);
  color: #f8fafc;
}

.member-center-dialog {
  position: relative;
  min-height: 100vh;
  padding: 3.42857rem 2.85714rem 4rem;
  background:
    radial-gradient(circle at top center, rgba(109, 56, 255, 0.16), transparent 30%),
    #06070b;
}

.member-center-close {
  position: absolute;
  top: 1.71429rem;
  right: 2.28571rem;
  z-index: 2;
  width: 2.42857rem;
  height: 2.42857rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ~"max(12PX, 1rem)";
  line-height: 1;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
  }
}

.member-center-content {
  // width: min(84.28571rem, calc(100vw - 5.71429rem));
  margin: 0 2.4rem;
  padding-top: 0.57143rem;
}

.profile-card {
  display: grid;
  grid-template-columns: minmax(16rem, 1.1fr) minmax(32rem, 3fr) auto;
  align-items: center;
  gap: 2rem;
  min-height: 9.71429rem;
  margin-bottom: 1.14286rem;
  padding: 1.71429rem 2rem;
  border: 1px solid rgba(51, 56, 78, 0.85);
  border-radius: 0.71429rem;
  background: #11121b;
}

.profile-card__identity {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 1.14286rem;
}

.profile-card__avatar {
  display: flex;
  flex: 0 0 4.85714rem;
  align-items: center;
  justify-content: center;
  width: 4.85714rem;
  height: 4.85714rem;
  border: 2px solid #8b5cf6;
  border-radius: 50%;
  background: #242033;
  color: #fff;
  font-size: ~"max(18PX, 1.71429rem)";
  font-weight: 800;
}

.profile-card__name-wrap {
  min-width: 0;

  h1 {
    overflow: hidden;
    margin: 0 0 0.57143rem;
    color: #fff;
    font-size: ~"max(18PX, 1.57143rem)";
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: inline-block;
    padding: 0.28571rem 0.57143rem;
    border-radius: 0.28571rem;
    background: rgba(255, 196, 0, 0.12);
    color: #ffc400;
    font-size: ~"max(12PX, 0.78571rem)";
  }
}

.profile-card__details {
  display: grid;
  grid-template-columns: repeat(4, minmax(7.14286rem, 1fr));
  gap: 1.14286rem;
  min-width: 0;
  margin: 0;

  div {
    min-width: 0;
  }

  dt {
    margin-bottom: 0.57143rem;
    color: #7f8ba8;
    font-size: ~"max(12PX, 0.85714rem)";
  }

  dd {
    overflow: hidden;
    margin: 0;
    color: #f8fafc;
    font-size: ~"max(12PX, 1rem)";
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.profile-card__edit {
  min-width: 9.14286rem;
  min-height: 3.14286rem;
  padding: 0.71429rem 1.14286rem;
  border: 1px solid #7c3aed;
  border-radius: 0.57143rem;
  background: transparent;
  color: #c4b5fd;
  font-size: ~"max(12PX, 0.92857rem)";
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(124, 58, 237, 0.12);
    color: #fff;
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.45);
  }
}

.profile-editor-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3010;
  background: rgba(0, 0, 0, 0.68);
}

.profile-editor {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 34.28571rem;
  height: 100%;
  overflow: hidden;
  border-left: 1px solid rgba(255, 255, 255, 0.09);
  background: #11121b;
  color: #f8fafc;
  box-shadow: -1.14286rem 0 2.85714rem rgba(0, 0, 0, 0.35);
}

.profile-editor__header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.42857rem 1.71429rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  h2 {
    margin: 0;
    font-size: ~"max(18PX, 1.42857rem)";
    line-height: 1.4;
  }

  p {
    margin: 0.28571rem 0 0;
    color: #8f9ab3;
    font-size: ~"max(12PX, 0.85714rem)";
  }
}

.profile-editor__close {
  width: 3.14286rem;
  height: 3.14286rem;
  margin: -0.71429rem -0.71429rem 0 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #a78bfa;
  font-size: 1.71429rem;
  line-height: 1;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: #fff;
    outline: none;
  }
}

.profile-editor__form {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 1.42857rem;
  min-height: 0;
  overflow-y: auto;
  padding: 2rem 1.71429rem 0;
}

.profile-editor__field {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.57143rem;
  color: #d8deea;
  font-size: ~"max(12PX, 0.92857rem)";

  input,
  .profile-editor__readonly {
    box-sizing: border-box;
    width: 100%;
    min-height: 3.42857rem;
    border: 1px solid #343849;
    border-radius: 0.57143rem;
    background: #191b26;
    color: #f8fafc;
    font: inherit;
  }

  input {
    padding: 0 1rem;
    outline: none;

    &:focus {
      border-color: #8b5cf6;
      box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
    }
  }

  small {
    align-self: flex-end;
    margin-top: -0.28571rem;
    color: #8f9ab3;
    font-size: ~"max(12PX, 0.78571rem)";
  }
}

.profile-editor__required {
  color: #fb7185;
  font-style: normal;
}

.profile-editor__readonly {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 1rem;

  span {
    overflow: hidden;
    color: #9aa3b5;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

}

.profile-editor__phone-toggle {
  flex: 0 0 auto;
  min-width: 3.71429rem;
  min-height: 2.85714rem;
  margin-right: -0.57143rem;
  padding: 0 0.57143rem;
  border: 1px solid #454a5c;
  border-radius: 0.42857rem;
  background: #272a37;
  color: #d8deea;
  font-size: ~"max(12PX, 0.78571rem)";
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: #8b5cf6;
    color: #fff;
    outline: none;
  }
}

.profile-editor__readonly-hint {
  margin: -0.57143rem 0 1rem;
  color: #727b90;
  font-size: ~"max(12PX, 0.78571rem)";
}

.profile-editor__footer {
  position: sticky;
  bottom: 0;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 0.85714rem;
  margin: auto -1.71429rem 0;
  padding: 1.14286rem 1.71429rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #11121b;

  button {
    min-height: 3.42857rem;
    border: 1px solid #454a5c;
    border-radius: 0.57143rem;
    background: transparent;
    color: #f8fafc;
    font-size: ~"max(12PX, 0.92857rem)";
    font-weight: 700;
    cursor: pointer;

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.45);
    }
  }

  .profile-editor__save {
    border-color: #7c3aed;
    background: #7c3aed;
    color: #fff;

    &:disabled {
      border-color: #3f3d49;
      background: #3f3d49;
      color: #8f8b9b;
      cursor: not-allowed;
    }
  }
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1.35fr;
  gap: 1.28571rem;
  margin-bottom: 2rem;
}

.points-card,
.plan-card {
  border-radius: 0.85714rem;
  background: #11121b;
  border: 1px solid rgba(51, 56, 78, 0.85);
}

.points-card {
  padding: 2rem 2.14286rem 1.71429rem;
}

.points-card__total {
  display: flex;
  align-items: baseline;
  gap: 0.71429rem;
}

.points-card__total-value {
  color: #ffc400;
  font-size: ~"max(12PX, 3rem)";
  font-weight: 900;
  line-height: 1;
}

.points-card__total-label {
  color: #8b93a7;
  font-size: ~"max(12PX, 1rem)";
}

.points-card__divider {
  height: 1px;
  margin: 1.57143rem 0 1.42857rem;
  background: rgba(255, 255, 255, 0.06);
}

.points-card__breakdown {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.28571rem;
}

.points-card__breakdown--recharge-only {
  grid-template-columns: 1fr;
}

.points-card__item-head {
  display: flex;
  align-items: center;
  gap: 0.57143rem;
  color: #d8deea;
  font-size: ~"max(12PX, 1rem)";
}

.dot {
  width: 0.57143rem;
  height: 0.57143rem;
  border-radius: 50%;

  &--gold {
    background: #ffc400;
  }

  &--gray {
    background: #64748b;
  }
}

.points-card__item-value {
  display: block;
  margin-top: 0.71429rem;
  color: #f8fafc;
  font-size: ~"max(12PX, 2.14286rem)";
  font-weight: 800;
  line-height: 1;

  &--gold {
    color: #ffc400;
  }
}

.points-card__item-note {
  margin: 0.57143rem 0 0;
  color: #6f778b;
  font-size: ~"max(12PX, 0.85714rem)";
  line-height: 1.5;
}

.points-card__add-seats-notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 22px 0 0;
  color: #f2b84b;
  font-size: 12px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.points-card__add-seats-help {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  margin-top: -2px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #f2b84b;
  font-size: 15px;
  line-height: 24px;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(242, 184, 75, 0.12);
    color: #ffd166;
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(242, 184, 75, 0.45);
  }
}

.plan-card {
  padding: 1.71429rem 2rem 1.57143rem;

  &--active {
    border-color: rgba(124, 58, 237, 0.75);
    box-shadow: 0 0 0 1px rgba(124, 58, 237, 0.18);
  }
}

.plan-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.14286rem;

  h2 {
    margin: 0;
    color: #ffffff;
    font-size: ~"max(12PX, 1.57143rem)";
    line-height: 2.14286rem;
  }
}

.plan-card__subtitle {
  margin: 0.42857rem 0 0;
  color: #6f778b;
  font-size: ~"max(12PX, 0.92857rem)";
}

.plan-card__badge {
  flex-shrink: 0;
  min-width: 4.14286rem;
  height: 2rem;
  padding: 0 0.85714rem;
  border-radius: 71.35714rem;
	background: #7c3aed;
	color: #ffffff;
  font-size: ~"max(12PX, 0.85714rem)";
  font-weight: 700;
  line-height: 2rem;
  text-align: center;

  &--inactive {
    background: rgba(255, 255, 255, 0.06);
    color: #94a3b8;
  }
}

.plan-card__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.14286rem;
  margin-top: 1.71429rem;
}

.plan-card__stat-label {
  display: block;
  color: #6f778b;
  font-size: ~"max(12PX, 0.92857rem)";
}

.plan-card__stat-value {
  display: block;
  margin-top: 0.71429rem;
  color: #f8fafc;
  font-size: ~"max(12PX, 1.71429rem)";
  font-weight: 800;
  line-height: 1.1;

  &--gold {
    color: #ffc400;
  }
}

.plan-card__stat-note {
  display: block;
  margin-top: 0.57143rem;
  color: #6f778b;
  font-size: ~"max(12PX, 0.85714rem)";
}

.plan-card__action {
  width: 7.14286rem;
  height: 2.85714rem;
  margin-top: 1.57143rem;
  border: none;
  border-radius: 0.57143rem;
  background: #ffc400;
  color: #0b0b0b;
  font-size: ~"max(12PX, 1rem)";
  font-weight: 500;
  cursor: pointer;
  transition: background 0.18s ease, box-shadow 0.18s ease;
}

.plan-card__add-action {
  width: 7.14286rem;
  height: 2.85714rem;
  margin-top: 1.57143rem;
  border: none;
  border-radius: 0.57143rem;
  background: #7c3aed;
  color: #fff;
	// margin-left: 0.85714rem;
	font-size: ~"max(12PX, 1rem)";
  font-weight: 500;
  cursor: pointer;
}
.plan-card__seat-action {
  width: 7.14286rem;
  height: 2.85714rem;
  margin-top: 1.57143rem;
  border: none;
  border-radius: 0.57143rem;
  background: #fff;
	border: 0.14286rem solid #7c3aed;
  color: #7c3aed;
	margin-left: 0.85714rem;
	font-size: ~"max(12PX, 1rem)";
  font-weight: 500;
  cursor: pointer;
}

.record-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 1.71429rem;
  margin-bottom: 1.28571rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.record-tabs__item {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.57143rem;
  padding: 0 0 1rem;
  border: none;
  background: transparent;
  color: #7d8496;
  font-size: ~"max(12PX, 1rem)";
  cursor: pointer;
  transition: color 0.18s ease;

  &.active {
    color: #ffffff;
    font-weight: 700;

    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1px;
      height: 0.14286rem;
      border-radius: 71.35714rem;
      background: #7c3aed;
    }
  }
}

.record-tabs__icon {
  font-size: ~"max(12PX, 1.07143rem)";
  line-height: 1;
}

.record-tabs__tag {
  padding: 0.14286rem 0.57143rem;
  border-radius: 71.35714rem;
  background: rgba(255, 196, 0, 0.12);
  color: #ffc400;
  font-size: ~"max(12PX, 0.78571rem)";
  font-weight: 700;
}

.records-panel {
  overflow: hidden;
  min-height: 0;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0.71429rem;
  background: #10111a;
  box-shadow: 0 1.14286rem 2.85714rem rgba(0, 0, 0, 0.18);
}

.records-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.14286rem;
  min-height: 4rem;
  padding: 0.71429rem 1rem;
}

.records-toolbar__count {
  color: #8b93a7;
  font-size: ~"max(12PX, 0.92857rem)";
}

.records-search {
  position: relative;
  display: flex;
  align-items: center;
  width: 17.14286rem;
}

.records-search__icon {
  position: absolute;
  left: 0.92857rem;
  color: #6f778b;
  font-size: ~"max(12PX, 0.92857rem)";
  pointer-events: none;
}

.records-search input {
  width: 100%;
  height: 2.57143rem;
  padding: 0 1rem 0 2.57143rem;
  border: 1px solid rgba(68, 58, 126, 0.35);
  border-radius: 0.57143rem;
  background: #121126;
  color: #f8fafc;
  font-size: ~"max(12PX, 0.92857rem)";
  outline: none;

  &::placeholder {
    color: #6f778b;
  }

  &:focus {
    border-color: rgba(124, 58, 237, 0.7);
  }
}

.records-table-wrap {
  overflow-x: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  background: transparent;
}

.records-table {
  width: 100%;
  min-width: 51.42857rem;
  border-collapse: collapse;

  th,
  td {
    height: 2.85714rem;
    padding: 0 1.28571rem;
    text-align: left;
    font-size: ~"max(12PX, 0.92857rem)";
    line-height: 1.42857rem;
    white-space: nowrap;
  }

  thead th {
    height: 2.85714rem;
    background: #191a2d;
    color: #8f96aa;
    font-weight: 700;
  }

  tbody tr {
    border-top: 1px solid rgba(255, 255, 255, 0.05);
  }

  tbody td {
    color: #eef2fb;
    font-weight: 700;
  }
}

.records-table__empty {
  padding: 3.42857rem 1.42857rem !important;
  text-align: center !important;
  color: #6f778b !important;
}

.records-table__points {
  font-weight: 700;

  &--gold {
    color: #ffc400;
  }

  &--plus {
    color: #00d29b;
  }

  &--minus {
    color: #fb7185;
  }
}

.records-table__integral-type {
  font-weight: 700;

  &--subscription {
    color: #ffc400;
  }

  &--recharge {
    color: #d8deea;
  }
}

.records-status {
  font-weight: 700;

  &--active {
    color: #c4b5fd;
  }

  &--received {
    color: #00d29b;
  }

  &--muted {
    color: #8b93a7;
  }
}

.records-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.42857rem;
  min-height: 3.71429rem;
  margin-top: 0;
  padding: 0.71429rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.records-pagination__total {
  margin-right: auto;
  color: #6f778b;
  font-size: ~"max(12PX, 0.92857rem)";
}

.records-pagination__nav,
.records-pagination__page {
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.57143rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.42857rem;
  background: rgba(14, 16, 24, 0.92);
  color: #9aa3b5;
  font-size: ~"max(12PX, 0.85714rem)";
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover:not(:disabled) {
    border-color: rgba(124, 58, 237, 0.45);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &.active {
    border-color: #7c3aed;
    background: #7c3aed;
    color: #ffffff;
    box-shadow: 0 0.42857rem 1.14286rem rgba(124, 58, 237, 0.35);
  }
}

@media (max-width: 960px) {
  .member-center-dialog {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .member-center-content {
    margin: 0;
  }

  .profile-card {
    grid-template-columns: 1fr auto;
  }

  .profile-card__details {
    grid-column: 1 / -1;
    grid-row: 2;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .plan-card__stats,
  .points-card__breakdown {
    grid-template-columns: 1fr;
  }

  .records-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .records-search {
    width: 100%;
  }
}

@media (max-width: 560px) {
  .profile-editor {
    max-width: none;
  }

  .profile-card {
    grid-template-columns: 1fr;
    gap: 1.28571rem;
    padding: 1.28571rem;
  }

  .profile-card__edit,
  .profile-card__details {
    grid-column: 1;
  }

  .profile-card__details {
    grid-row: auto;
    grid-template-columns: 1fr;
  }

  .profile-card__edit {
    width: 100%;
  }
}
</style>

<style lang="less">
.add-seats-points-help-tooltip {
  max-width: min(560px, calc(100vw - 32px));
  padding: 14px 16px;
  white-space: pre-line;
  word-break: break-word;
  font-size: 13px;
  line-height: 1.65;
}
</style>
